import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { de, fmt } from '../i18n/de'

interface NavItem {
  to: string
  icon: string
  label: string
  permission?: string
  superadminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: '▦', label: de.nav.dashboard },
  { to: '/doorcodes', icon: '⬢', label: de.nav.doorcodes },
  { to: '/groups', icon: '⬡', label: de.nav.groups },
  { to: '/doors', icon: '⬓', label: de.nav.doors },
  { to: '/accounts', icon: '⬒', label: de.nav.accounts, permission: 'accounts.read' },
  { to: '/log', icon: '≡', label: de.nav.log },
  { to: '/audit', icon: '⟳', label: de.nav.audit, permission: 'audit.read' },
]

const SETTINGS_ITEMS: NavItem[] = [
  { to: '/organizations', icon: '◈', label: de.nav.organizations, superadminOnly: true },
  { to: '/organization', icon: '⚙', label: de.nav.organization, permission: 'organization.write' },
  { to: '/profile', icon: '◔', label: de.nav.profile },
]

function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem('dam.theme') as 'light' | 'dark') ?? 'light')

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('dam.theme', theme)
  }, [theme])

  return { theme, toggle: () => setTheme(current => (current === 'light' ? 'dark' : 'light')) }
}

export function Layout() {
  const { me, can, logout, enterOrganization } = useAuth()
  const { toggle } = useTheme()
  const navigate = useNavigate()

  if (!me) return null

  const visible = (item: NavItem) =>
    (!item.superadminOnly || me.role === 'superadmin')
    && (!item.permission || can(item.permission))

  const leaveContext = async () => {
    await enterOrganization(null)
    navigate('/organizations')
  }

  // Im Superadmin-Kontext wird die Kopfzeile farblich abgesetzt, damit nicht unbemerkt in
  // einer fremden Organisation gearbeitet wird.
  const inContext = me.isOrganizationContext

  return (
    <div className="app">
      <header className={inContext ? 'sa-context' : undefined}>
        <div className="brand">
          <span className="logo">D</span> {de.app.name}
        </div>

        {me.organizationName && (
          <span className="org-switch" style={{ cursor: 'default' }}>
            {inContext
              ? fmt(de.organizations.contextActive, { name: me.organizationName })
              : me.organizationName}
          </span>
        )}

        {inContext && (
          <button className="btn btn-ghost" onClick={() => void leaveContext()}>
            {de.nav.leaveContext}
          </button>
        )}

        <div className="spacer" />

        <button className="btn btn-icon btn-ghost" onClick={toggle} title={de.nav.theme}>◐</button>

        <span className="avatar" title={me.displayName}>
          {initials(me.displayName || me.email)}
        </span>

        <button className="btn btn-ghost" onClick={() => void logout()}>{de.nav.logout}</button>
      </header>

      <nav>
        {NAV_ITEMS.filter(visible).map(item => (
          // NavLink setzt aria-current="page" selbst; darauf zielt der Stil in tokens.css.
          <NavLink key={item.to} to={item.to} end={item.to === '/'}>
            <span className="ico">{item.icon}</span>
            <span className="txt">{item.label}</span>
          </NavLink>
        ))}

        <hr />

        {SETTINGS_ITEMS.filter(visible).map(item => (
          <NavLink key={item.to} to={item.to}>
            <span className="ico">{item.icon}</span>
            <span className="txt">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <main>
        <Outlet />
      </main>
    </div>
  )
}

function initials(name: string): string {
  const parts = name.replace(',', '').split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

export function PageHeader({ title, subtitle, actions, back }: {
  title: string
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  back?: { to: string; label: string }
}) {
  const navigate = useNavigate()

  return (
    <>
      {back && (
        <div className="breadcrumb" onClick={() => navigate(back.to)}>‹ {back.label}</div>
      )}
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          {subtitle && <div className="sub">{subtitle}</div>}
        </div>
        <div className="spacer" />
        {actions}
      </div>
    </>
  )
}
